import { Badge, Box, Button, Group, Paper, Select, SimpleGrid, Text, TextInput, Title } from "@mantine/core";
import { useForm, isEmail } from "@mantine/form";
import useSWR from "swr";
import { fetcher } from "../../../../services/fetcher";
import { AssignCover_API, WalletEndpoint_API } from "../../../../api/endpoints";
import { Link } from "react-router-dom";
import { useMediaQuery } from "@mantine/hooks";

const UserForm = ({ onSubmit, data, groups, departments, mode }) => {
  const form = useForm({
    initialValues: {
      id: data.id || "",
      email: data.user_profile_info.user.email || "",
      first_name: data.user_profile_info.user.first_name || "",
      last_name: data.user_profile_info.user.last_name || "",
      address: data.user_profile_info.address || "",
      state: data.user_profile_info.state || "",
      city: data.user_profile_info.city || "",
      pin: data.user_profile_info.pin || "",
      tel_no: data.user_profile_info.tel_no || "",
      gender: data.user_profile_info.gender || "",
      date_of_birth: data.user_profile_info.date_of_birth || "",
      department_name: data.department_name || "",
      job_title: data.job_title || "",
      manager_name: data.manager_name || "",
      employment_status: data.employment_status || "",
      date_of_hire: data.date_of_hire || "",
      work_schedule: data.work_schedule || "",
      performance_rating: data.performance_rating || "",
      pf_number: data.pf_number || "",
      roles: data.permission || [],
    },

    validate: {
      email: isEmail("Invalid email"),
    },
  });

  const isEditMode = mode === "edit";
  const userInfo = data?.user_profile_info?.user;
  const firstName = userInfo?.first_name || "New";
  const lastName = userInfo?.last_name || "User";
  const initials = `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  const isActive = Boolean(userInfo?.is_active);
  const departmentName = data?.department_name || "Department";
  const userCode = data?.code || "----";
  const userEmail = userInfo?.email;

  const { data: assignedPlans } = useSWR(
    userEmail ? `${AssignCover_API}?user_email=${userEmail}` : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  const { data: wallet } = useSWR(
    userEmail ? `${WalletEndpoint_API}?email=${userEmail}` : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  const activeCovers = Array.isArray(assignedPlans?.customize_covers)
    ? assignedPlans.customize_covers
    : [];
  const visibleCovers = activeCovers.slice(0, 1).map((cover) => cover.title);
  const hasMoreCovers = activeCovers.length > 1;
  const coversCount = activeCovers.length;

  const isSmall = useMediaQuery("(max-width: 640px)");
  const isMedium = useMediaQuery("(max-width: 1024px)");

  const styles = {
    page: { display: "flex", flexDirection: "column", gap: 24 },
    summaryCard: {
      background: "#fff",
      padding: 24,
      border: "1px solid #e9eff6",
      boxShadow: "0 4px 24px rgba(9, 44, 76, 0.06)",
    },
    summaryTop: {
      display: "flex",
      justifyContent: "space-between",
      gap: 24,
      flexWrap: "wrap",
      flexDirection: isSmall ? "column" : "row",
      alignItems: isSmall ? "flex-start" : "center",
    },
    userInfo: { display: "flex", alignItems: "center", gap: 16 },
    avatar: {
      width: 64,
      height: 64,
      borderRadius: 18,
      background: "linear-gradient(135deg, #1f7ae0 0%, #0b5fbf 100%)",
      color: "#fff",
      fontWeight: 700,
      fontSize: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      letterSpacing: 1,
      boxShadow: "0 8px 18px rgba(11, 95, 191, 0.3)",
    },
    userName: { color: "#092c4c", fontWeight: 600, fontSize: 20 },
    metaRow: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 4 },
    metaText: { color: "#6c94b4", fontSize: 14, fontWeight: 500 },
    activeBadge: { background: "#e7fff2", color: "#0a9b5a", border: "none", fontWeight: 600 },
    stats: {
      display: "flex",
      alignItems: "center",
      gap: 24,
      width: isSmall ? "100%" : "auto",
      justifyContent: isSmall ? "space-between" : "flex-start",
      flexWrap: "wrap",
    },
    statItem: { textAlign: "center", minWidth: 90 },
    statValue: { color: "#092c4c", fontWeight: 700, fontSize: 20, lineHeight: 1.2 },
    statLabel: { color: "#8aa2b3", fontSize: 12, fontWeight: 500 },
    statDivider: { width: 1, height: 40, background: "#e5edf5" },
    contentGrid: {
      display: "grid",
      gridTemplateColumns: isMedium ? "1fr" : "minmax(0, 1fr) 320px",
      gap: 24,
    },
    userFormPaper: { padding: "1.5rem" },
    heading: { marginLeft: "1rem", color: "#333", fontFamily: "Montserrat", fontSize: "1.5rem", fontWeight: 600 },
    userProfile: { padding: isSmall ? "18px 8px" : isMedium ? "18px 24px" : "24px 48px" },
    sideStack: { display: "flex", flexDirection: "column", gap: 16 },
    sideCard: { background: "#fff", border: "1px solid #e9eff6", padding: 16 },
    sideHeader: { alignItems: "center", marginBottom: 10 },
    headerDot: { width: 8, height: 8, borderRadius: 999, background: "#0b5fbf", display: "inline-block" },
    sideTitle: { color: "#092c4c", fontWeight: 600, fontSize: 14 },
    sideRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0" },
    sideLabel: { color: "#8aa2b3", fontSize: 13, fontWeight: 500 },
    sideValue: { color: "#092c4c", fontSize: 13, fontWeight: 600 },
    coverValue: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, textAlign: "right" },
    viewMore: { color: "#0b5fbf", fontSize: 12, fontWeight: 600, textDecoration: "none" },
    walletLabel: { color: "#8aa2b3", fontSize: 13, fontWeight: 500, marginTop: 4 },
    walletValue: { color: "#092c4c", fontWeight: 700, fontSize: 24, marginTop: 4 },
  };

  return (
    <Box style={styles.page}>
      <Paper radius="xl" style={styles.summaryCard} mt="lg">
        <div style={styles.summaryTop}>
          <div style={styles.userInfo}>
            <div style={styles.avatar}>{initials || "NU"}</div>
            <div>
              <Text style={styles.userName}>
                {firstName} {lastName}
              </Text>
              <div style={styles.metaRow}>
                <Text component="span" style={styles.metaText}>
                  {departmentName}
                </Text>
                <Text component="span" style={styles.metaText}>
                  ID {userCode}
                </Text>
                <Badge size="sm" styles={{ root: styles.activeBadge }}>
                  {isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
          </div>

          <div style={styles.stats}>
            <div style={styles.statItem}>
              <Text style={styles.statValue}>0</Text>
              <Text style={styles.statLabel}>Policies Issued</Text>
            </div>
            <div style={styles.statDivider} />
            <div style={styles.statItem}>
              <Text style={styles.statValue}>{coversCount}</Text>
              <Text style={styles.statLabel}>Covers</Text>
            </div>
            <div style={styles.statDivider} />
            <div style={styles.statItem}>
              <Text style={styles.statValue}>₹0</Text>
              <Text style={styles.statLabel}>Total Payments</Text>
            </div>
          </div>
        </div>
      </Paper>

      <div style={styles.contentGrid}>
        <form
          onSubmit={form.onSubmit((values) => {
            const formattedValues = {
              ...values,
              date_of_hire: values.date_of_hire
                ? new Date(values.date_of_hire).toISOString().split("T")[0]
                : "",
              date_of_birth: values.date_of_birth
                ? new Date(values.date_of_birth).toISOString().split("T")[0]
                : "",
            };
            onSubmit(formattedValues);
          })}
        >
          <Paper shadow="xl" style={styles.userFormPaper}>
            <Title style={styles.heading}>Employee Details</Title>
            <SimpleGrid
              mt="lg"
              cols={{ base: 1, sm: 2, lg: 2, xl: 2 }}
              spacing={{ base: 10, xs: "xl", sm: "xl" }}
              style={styles.userProfile}
            >
              <TextInput
                placeholder={"Enter job title"}
                label={"Job Title"}
                type={"text"}
                size={"sm"}
                disabled={!isEditMode}
                {...form.getInputProps("job_title")}
              />

              <Select
                label="Department"
                placeholder="Select department"
                data={departments}
                {...form.getInputProps("department_name")}
                disabled={!isEditMode}
              />

              <TextInput
                placeholder={"Enter manager name"}
                label={"Manager Name"}
                type={"text"}
                size={"sm"}
                disabled={!isEditMode}
                {...form.getInputProps("manager_name")}
              />

              <Select
                label="Employment Status"
                placeholder="Select Employment Status"
                data={["Full-time", "Part-time", "Contract"]}
                {...form.getInputProps("employment_status")}
                disabled={!isEditMode}
              />

              <TextInput
                label="Date of Hire"
                type="date"
                placeholder="Enter date of hire"
                disabled={!isEditMode}
                {...form.getInputProps("date_of_hire")}
              />

              {/* <TextInput
                placeholder={"Enter PF number"}
                label={"PF Number"}
                type={"text"}
                size={"sm"}
                disabled={true}
                {...form.getInputProps("pf_number")}
              /> */}

              <Select
                label="Roles"
                data={groups}
                placeholder="Select user roles"
                disabled={!isEditMode}
                {...form.getInputProps("roles")}
              />
            </SimpleGrid>
          </Paper>

          <Paper shadow="xl" mt="xl" style={styles.userFormPaper}>
            <Title style={styles.heading}>Profile Details</Title>
            <SimpleGrid
              mt="lg"
              cols={{ base: 1, md: 2, lg: 2, xl: 2 }}
              spacing={{ base: 10, xs: "xl", sm: "xl" }}
              style={styles.userProfile}
            >
              <TextInput
                placeholder={"Enter user first name"}
                label={"First name"}
                type={"text"}
                size={"sm"}
                disabled={!isEditMode}
                {...form.getInputProps("first_name")}
              />

              <TextInput
                placeholder={"Enter user last name"}
                label={"Last name"}
                type={"text"}
                size={"sm"}
                disabled={!isEditMode}
                {...form.getInputProps("last_name")}
              />

              <TextInput
                placeholder={"Enter user email"}
                label={"Email"}
                type={"text"}
                size={"sm"}
                disabled={!isEditMode}
                {...form.getInputProps("email")}
              />

              <TextInput
                label="Date of Birth"
                type="date"
                placeholder="Enter date of birth"
                disabled={!isEditMode}
                {...form.getInputProps("date_of_birth")}
              />

              <Select
                label="Gender"
                data={["Male", "Female", "Other"]}
                placeholder="Select Gender"
                {...form.getInputProps("gender")}
                disabled={!isEditMode}
              />

              <TextInput
                placeholder={"Enter mobile number"}
                label={"Phone No"}
                type={"text"}
                size={"sm"}
                disabled={!isEditMode}
                {...form.getInputProps("tel_no")}
              />

              <TextInput
                placeholder={"Enter Address"}
                label={"Address"}
                type={"text"}
                size={"sm"}
                disabled={!isEditMode}
                {...form.getInputProps("address")}
              />

              <TextInput
                placeholder={"Enter Pincode"}
                label={"Pin"}
                type={"number"}
                size={"sm"}
                disabled={!isEditMode}
                {...form.getInputProps("pin")}
              />

              <TextInput
                placeholder={"Enter City"}
                label={"City"}
                type={"text"}
                size={"sm"}
                disabled={!isEditMode}
                {...form.getInputProps("city")}
              />

              <TextInput
                placeholder={"Enter State"}
                label={"State"}
                type={"text"}
                size={"sm"}
                disabled={!isEditMode}
                {...form.getInputProps("state")}
              />
            </SimpleGrid>
            {isEditMode && (
              <Box style={{ textAlign: "center" }}>
                <Button variant="outline" type="submit" mt="xl" size="md">
                  Save
                </Button>
              </Box>
            )}
          </Paper>
        </form>

        <div style={styles.sideStack}>
          <Paper radius="lg" style={styles.sideCard}>
            <Group gap="xs" style={styles.sideHeader}>
              <span style={styles.headerDot} />
              <Text style={styles.sideTitle}>Wallet Snapshot</Text>
            </Group>
            <Text style={styles.walletLabel}>Available Balance</Text>
            <Text style={styles.walletValue}>
              {wallet?.currency_type || "₹"}
              {wallet?.balance ?? 0}
            </Text>
          </Paper>
           <Paper radius="lg" style={styles.sideCard}>
            <Group gap="xs" style={styles.sideHeader}>
              <span style={styles.headerDot} />
              <Text style={styles.sideTitle}>Account Summary</Text>
            </Group>
            <div style={styles.sideRow}>
              <Text style={styles.sideLabel}>Active Covers</Text>
              <div style={styles.coverValue}>
                <Text style={styles.sideValue}>
                  {visibleCovers.length > 0 ? visibleCovers.join(", ") : "-"}
                </Text>
                {hasMoreCovers && data?.id && (
                  <Link
                    style={styles.viewMore}
                    to={`/user/users/${data.id}/assign-covers`}
                  >
                    View all
                  </Link>
                )}
              </div>
            </div>
          </Paper>
        </div>
      </div>
    </Box>
  );
};

export default UserForm;